import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Leaf,
  ArrowLeft,
  LogOut,
  Eye,
  Package,
  CheckCircle,
  Clock,
  Truck,
  XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const auth = sessionStorage.getItem("adminAuth");
  if (!auth) return null;
  return { Authorization: `Basic ${auth}` };
};

const statusColors = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const statusIcons = {
  pending: Clock,
  confirmed: CheckCircle,
  shipped: Truck,
  delivered: Package,
  cancelled: XCircle,
};

export const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const headers = getAuthHeaders();

  useEffect(() => {
    if (!headers) {
      navigate("/admin");
      return;
    }
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, { headers });
      setOrders(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        navigate("/admin");
      } else {
        toast.error("Failed to load orders");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, null, { headers });
      toast.success("Order status updated!");
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, order_status: status });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-cream animate-fade-in">
      {/* Header */}
      <header className="bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Leaf className="w-6 h-6" />
              <span className="font-serif text-xl font-semibold">WEH Admin</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 font-sans text-sm text-white/80 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            data-testid="back-to-dashboard"
            className="text-stone-500 hover:text-deep-moss"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-stone-900">Orders</h1>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white p-12 text-center border border-stone-200">
            <Package className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <p className="font-sans text-stone-500">No orders yet</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Orders List */}
            <div className="lg:col-span-2 bg-white border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Order</th>
                      <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left font-sans text-xs font-medium text-stone-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right font-sans text-xs font-medium text-stone-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    {orders.map((order) => {
                      const StatusIcon = statusIcons[order.order_status] || Clock;
                      return (
                        <tr
                          key={order.id}
                          data-testid={`order-row-${order.id}`}
                          className={selectedOrder?.id === order.id ? "bg-bone-cream" : ""}
                        >
                          <td className="px-4 py-3">
                            <p className="font-mono text-xs text-stone-900">{order.id.slice(0, 8)}...</p>
                            <p className="font-sans text-xs text-stone-500">
                              {format(new Date(order.created_at), "MMM d, yyyy")}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-sans text-sm text-stone-900">{order.customer_name}</p>
                            <p className="font-sans text-xs text-stone-500">{order.customer_email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-sans text-sm font-semibold text-stone-900">
                              ${order.total.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${statusColors[order.order_status]}`}>
                              <StatusIcon className="w-3 h-3" />
                              {order.order_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => setSelectedOrder(order)}
                              data-testid={`view-order-${order.id}`}
                              className="p-2 text-stone-500 hover:text-deep-moss"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Order Details */}
            <div className="lg:col-span-1">
              {selectedOrder ? (
                <div className="bg-white p-6 border border-stone-200 sticky top-24 animate-fade-in">
                  <h3 className="font-serif text-lg font-semibold text-stone-900 mb-4">
                    Order Details
                  </h3>

                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="font-sans text-xs text-stone-500 uppercase">Order ID</p>
                      <p className="font-mono text-sm text-stone-900 break-all">{selectedOrder.id}</p>
                    </div>
                    <div>
                      <p className="font-sans text-xs text-stone-500 uppercase">Customer</p>
                      <p className="font-sans text-sm text-stone-900">{selectedOrder.customer_name}</p>
                      <p className="font-sans text-sm text-stone-600">{selectedOrder.customer_email}</p>
                      {selectedOrder.customer_phone && (
                        <p className="font-sans text-sm text-stone-600">{selectedOrder.customer_phone}</p>
                      )}
                    </div>
                    <div>
                      <p className="font-sans text-xs text-stone-500 uppercase">Shipping Address</p>
                      <p className="font-sans text-sm text-stone-900 whitespace-pre-line">
                        {selectedOrder.shipping_address}
                      </p>
                    </div>
                    {selectedOrder.notes && (
                      <div>
                        <p className="font-sans text-xs text-stone-500 uppercase">Notes</p>
                        <p className="font-sans text-sm text-stone-900">{selectedOrder.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-stone-200 pt-4 mb-6">
                    <p className="font-sans text-xs text-stone-500 uppercase mb-2">Items</p>
                    <div className="space-y-2">
                      {selectedOrder.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between font-sans text-sm">
                          <span className="text-stone-600">
                            {item.name} × {item.quantity}
                          </span>
                          <span className="text-stone-900">${item.subtotal.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between font-sans text-sm font-semibold mt-4 pt-4 border-t border-stone-200">
                      <span>Total</span>
                      <span>${selectedOrder.total.toFixed(2)}</span>
                    </div>
                  </div>

                  <div>
                    <p className="font-sans text-xs text-stone-500 uppercase mb-2">Update Status</p>
                    <select
                      value={selectedOrder.order_status}
                      onChange={(e) => updateStatus(selectedOrder.id, e.target.value)}
                      data-testid="update-status-select"
                      className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="bg-white p-12 text-center border border-stone-200">
                  <Eye className="w-8 h-8 mx-auto text-stone-300 mb-2" />
                  <p className="font-sans text-sm text-stone-500">
                    Select an order to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
